var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Blockchain, Utils, useApiSession, useAuth, useSessionContext, useUserContext } from '@dfx.swiss/react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from '../hooks/store.hook';
import { useAppHandlingContext } from './app-handling.context';
import { useBalanceContext } from './balance.context';
const SignatureFormat = /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[A-Fa-f0-9]{64})$/;
export var WalletType;
(function (WalletType) {
    WalletType["META_MASK"] = "MetaMask";
    WalletType["ALBY"] = "Alby";
    WalletType["LEDGER_BTC"] = "LedgerBtc";
    WalletType["LEDGER_ETH"] = "LedgerEth";
    WalletType["BITBOX_BTC"] = "BitBoxBtc";
    WalletType["BITBOX_ETH"] = "BitBoxEth";
    WalletType["TREZOR_BTC"] = "TrezorBtc";
    WalletType["TREZOR_ETH"] = "TrezorEth";
    WalletType["CLI_BTC"] = "CliBtc";
    WalletType["CLI_XMR"] = "CliXmr";
    WalletType["CLI_ETH"] = "CliEth";
    WalletType["CLI_ADA"] = "CliAda";
    WalletType["CLI_AR"] = "CliAr";
    WalletType["CLI_LN"] = "CliLn";
    WalletType["DFX_TARO"] = "DfxTaro";
    WalletType["WALLET_CONNECT"] = "WalletConnect";
    WalletType["CAKE"] = "Cake";
    WalletType["MONERO"] = "Monero";
    WalletType["MAIL"] = "Mail";
})(WalletType || (WalletType = {}));
export const WalletBlockchains = {
    [WalletType.META_MASK]: [
        Blockchain.ETHEREUM,
        Blockchain.ARBITRUM,
        Blockchain.OPTIMISM,
        Blockchain.POLYGON,
        Blockchain.BASE,
        Blockchain.HAQQ,
        Blockchain.BINANCE_SMART_CHAIN,
    ],
    [WalletType.ALBY]: [Blockchain.LIGHTNING],
    [WalletType.LEDGER_BTC]: [Blockchain.BITCOIN],
    [WalletType.LEDGER_ETH]: [Blockchain.ETHEREUM, Blockchain.ARBITRUM, Blockchain.OPTIMISM, Blockchain.POLYGON],
    [WalletType.BITBOX_BTC]: [Blockchain.BITCOIN],
    [WalletType.BITBOX_ETH]: [Blockchain.ETHEREUM, Blockchain.ARBITRUM, Blockchain.OPTIMISM, Blockchain.POLYGON],
    [WalletType.TREZOR_BTC]: [Blockchain.BITCOIN],
    [WalletType.TREZOR_ETH]: [Blockchain.ETHEREUM, Blockchain.ARBITRUM, Blockchain.OPTIMISM, Blockchain.POLYGON],
    [WalletType.CLI_BTC]: [Blockchain.BITCOIN],
    [WalletType.CLI_LN]: [Blockchain.LIGHTNING],
    [WalletType.CLI_XMR]: [Blockchain.MONERO],
    [WalletType.CLI_ETH]: [
        Blockchain.ETHEREUM,
        Blockchain.ARBITRUM,
        Blockchain.OPTIMISM,
        Blockchain.POLYGON,
        Blockchain.BASE,
        Blockchain.HAQQ,
        Blockchain.BINANCE_SMART_CHAIN,
    ],
    [WalletType.CLI_ADA]: [Blockchain.CARDANO],
    [WalletType.CLI_AR]: [Blockchain.ARWEAVE],
    [WalletType.DFX_TARO]: [Blockchain.LIGHTNING],
    [WalletType.WALLET_CONNECT]: [
        Blockchain.ETHEREUM,
        Blockchain.ARBITRUM,
        Blockchain.OPTIMISM,
        Blockchain.POLYGON,
        Blockchain.BASE,
        Blockchain.HAQQ,
        Blockchain.BINANCE_SMART_CHAIN,
    ],
    [WalletType.CAKE]: [Blockchain.MONERO],
    [WalletType.MONERO]: [Blockchain.MONERO],
};
export function supportsBlockchain(wallet, blockchain) {
    const supportedChains = WalletBlockchains[wallet];
    return !supportedChains || supportedChains.includes(blockchain);
}
const WalletContext = createContext(undefined);
export function useWalletContext() {
    return useContext(WalletContext);
}
export function WalletContextProvider(props) {
    const { isInitialized: isSessionInitialized, isLoggedIn, logout } = useSessionContext();
    const { updateSession } = useApiSession();
    const api = useSessionContext();
    const { isInitialized: isParamsInitialized, params: appParams } = useAppHandlingContext();
    const { getSignMessage } = useAuth();
    const { readBalances } = useBalanceContext();
    const { activeWallet: activeWalletStore } = useStore();
    const { addDiscountCode } = useUserContext();
    const [isInitialized, setIsInitialized] = useState(false);
    const [activeWallet, setActiveWallet] = useState(activeWalletStore.get());
    const [activeBlockchain, setActiveBlockchain] = useState();
    // initialize
    useEffect(() => {
        if (isSessionInitialized && !isLoggedIn) {
            setWallet();
            if (isInitialized)
                readBalances(undefined);
        }
        if (isSessionInitialized && isInitialized && isLoggedIn && appParams.specialCode) {
            addDiscountCode(appParams.specialCode).catch(() => undefined);
        }
    }, [isSessionInitialized, isLoggedIn, isInitialized]);
    useEffect(() => {
        if (isParamsInitialized)
            handleParamSession().then((hasSession) => {
                if (hasSession) {
                    setWallet(appParams.type);
                    appParams.redirect && props.router.navigate(appParams.redirect);
                }
                setIsInitialized(true);
            });
    }, [isParamsInitialized]);
    function handleParamSession() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (appParams.address && appParams.signature) {
                    if (!SignatureFormat.test(appParams.signature))
                        throw new Error('Invalid signature');
                    yield createSession(appParams.address, appParams.signature);
                    return true;
                }
                else if (appParams.session && Utils.isJwt(appParams.session)) {
                    updateSession(appParams.session);
                    return true;
                }
            }
            catch (e) {
                logout();
            }
            return false;
        });
    }
    function setWallet(walletType) {
        setActiveWallet(walletType);
        walletType ? activeWalletStore.set(walletType) : activeWalletStore.remove();
    }
    // public API
    function login(wallet, address, blockchain, onSignMessage, key) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const message = yield getSignMessage(address);
                const signature = yield onSignMessage(address, message);
                yield createSession(address, signature, key);
            }
            catch (e) {
                api.logout();
                setWallet();
                throw e;
            }
            setWallet(wallet);
            setActiveBlockchain(blockchain);
        });
    }
    function setSession(wallet, blockchain, session) {
        return __awaiter(this, void 0, void 0, function* () {
            updateSession(session);
            setWallet(wallet);
            setActiveBlockchain(blockchain);
        });
    }
    function createSession(address, signature, key) {
        return __awaiter(this, void 0, void 0, function* () {
            return api.authenticate(address, signature, key, appParams.specialCode, appParams.wallet, appParams.refcode);
        });
    }
    const context = useMemo(() => ({
        isInitialized: isInitialized && isSessionInitialized && isParamsInitialized,
        blockchain: activeBlockchain,
        switchBlockchain: setActiveBlockchain,
        login,
        setSession,
        activeWallet,
    }), [isInitialized, isSessionInitialized, isParamsInitialized, activeWallet, activeBlockchain, api, appParams]);
    return _jsx(WalletContext.Provider, Object.assign({ value: context }, { children: props.children }));
}
